import React, { createContext, useContext, useState, useMemo } from 'react';
import moment, { isMoment } from 'moment-timezone';
import { DataGridAdvancedFilter } from 'common/types/datagrid';
import { displayDate } from 'common/utils/date-time';
import { DisplayType } from 'common/types';
import { DropDownSelect } from 'common/types/form';

export interface FilterOption {
    id: string;
    display: string;
}

type FilterComponentType =
    | 'checkbox'
    | 'text'
    | 'select'
    | 'date'
    | 'checkbox-group'
    | 'radio';

type FilterTermType<T extends FilterComponentType> = T extends 'checkbox'
    ? boolean
    : T extends 'text' | 'checkbox-group' | 'radio'
      ? string
      : T extends 'select'
        ? string | number
        : T extends 'date'
          ? moment.Moment | string
          : unknown;

interface DataGridAdvancedFilterBase<T = unknown> {
    display: string;
    term: T;
    form?: DisplayType<string | boolean> & {
        segmentId?: string;
        reasons?: {};
        parentId?: number;
        id?: string;
    };
    isDefault?: boolean;
    distance?: {
        id: number;
        display: string;
    };
}

export type DataGridAdvancedFilterExtended<T extends FilterComponentType> =
    DataGridAdvancedFilterBase & {
        term: FilterTermType<T>;
        componentType?: T;
    };

type AdvancedFilterComponentTypes = {
    // Arrays
    priority: 'checkbox-group';
    status: 'select';
    issue: 'select';
    issueReasonIds: 'select';
    issueReason: 'select';
    issueSecondaryReason: 'select';
    financialStatus: 'select';
    fulfillment: 'checkbox-group';
    trade: 'checkbox-group';
    lineOfBusiness: 'checkbox-group';
    segments: 'select';
    orderType: 'select';
    logistic: 'checkbox-group';
    returnStatus: 'select';
    excludeClient: 'select';
    state: 'select';
    orderedOnBehalfClientId: 'select';
    payeeClientId: 'select';
    tradeType: 'checkbox-group';
    fulfillmentType: 'checkbox-group';
    invoiceStatus: 'select' | 'checkbox-group';
    type: 'select';
    project: 'select';
    sku: 'select';

    // Singles
    jobType: 'radio';
    salesInvoiceNumber: 'text';
    purchaseInvoiceNumber: 'text';
    orderedBefore: 'date';
    orderedAfter: 'date';
    planned: 'radio';
    occupied: 'radio';
    directSupply: 'checkbox';
    integrated: 'checkbox';
    equipmentOnly: 'checkbox';
    laborWarranty: 'checkbox';
    hasEquipmentWarrantyAssets: 'checkbox';
    vendorOrderNumberExists: 'radio';
    vendorVerified: 'radio';
    localTruckCheckBoxAutomated: 'radio';
    manualTrackingNumber: 'radio';
    localTruckCheckBoxManual: 'radio';
    country: 'radio';
    ownerIs: 'radio';
    createdBefore: 'date';
    createdAfter: 'date';
    availableBefore: 'date';
    availableAfter: 'date';
    shouldSort: 'checkbox';
    activeStatus: 'radio';
    zip: 'text';
    radius: 'text';
    distance: 'radio';
    eligibility: 'radio';
    _tradeType: 'radio';
    _tradeSubType: 'radio';
    _fulfillmentType: 'radio';
};

type AdvancedFiltersState = {
    // filter keys with values of type array need to be added here
    [K in keyof AdvancedFilterComponentTypes]: K extends
        | 'priority'
        | 'status'
        | 'issue'
        | 'issueReasonIds'
        | 'issueReason'
        | 'issueSecondaryReason'
        | 'financialStatus'
        | 'fulfillment'
        | 'trade'
        | 'lineOfBusiness'
        | 'segments'
        | 'orderType'
        | 'logistic'
        | 'returnStatus'
        | 'excludeClient'
        | 'state'
        | 'orderedOnBehalfClientId'
        | 'payeeClientId'
        | 'tradeType'
        | 'fulfillmentType'
        | 'invoiceStatus'
        | 'type'
        | 'project'
        | 'sku'
        ? DataGridAdvancedFilterExtended<AdvancedFilterComponentTypes[K]>[]
        : DataGridAdvancedFilterExtended<AdvancedFilterComponentTypes[K]>;
} & {
    // Allow dynamic keys for new filter types
    [key: string]:
        | DataGridAdvancedFilter
        | DataGridAdvancedFilter[]
        | undefined;
};

export type { AdvancedFiltersState };

type AdvancedFiltersContextType = {
    advancedFilters: Partial<AdvancedFiltersState>;
    setAdvancedFilters: React.Dispatch<
        React.SetStateAction<Partial<AdvancedFiltersState>>
    >;
    totalAdvancedFilterCount: Record<string, number>;
    setTotalAdvancedFilterCount: React.Dispatch<
        React.SetStateAction<Record<string, number>>
    >;
    sectionExpansionStatus: Record<string, boolean>;
    setSectionExpansionStatus: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
    handleDropdownSelectCheckboxChange: (
        values: DropDownSelect[],
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleFormSelectChangeMultiple: (
        values: FilterOption[],
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleFormSelectChangeSingle: (
        values: FilterOption | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleCheckboxGroupChange: (
        values: string[],
        target: string,
        display?: string,
        options?: FilterOption[],
        isDefault?: boolean
    ) => void;
    handleCheckboxChange: (
        value: boolean | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleDateChange: (
        date: moment.Moment | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleGreaterLessThanChange: (
        value: string | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleRadioButtonGroupChange: (
        value: string | number | boolean | null,
        target: string,
        display?: string,
        isDefault?: boolean,
        overrideDisplay?: boolean
    ) => void;
    handleZipCodeDistanceChange: (
        zip: string | null,
        distance: { id: number; display: string } | null,
        target: string,
        isDefault?: boolean
    ) => void;
    handleToggleButtonGroupChange: (
        value: { id: string; display: string; value?: boolean } | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => void;
    handleTextInputChange: (
        value: string | null,
        target: string,
        display?: string,
        isDefault?: boolean,
        overrideDisplay?: boolean
    ) => void;
    handleCustomRangeDefaultChange: (
        value: string,
        target: string,
        display: string,
        dateKeys: string[],
        isDefault: boolean,
        isClear?: boolean
    ) => void;
    handleCustomRangeChange: (
        defaultFilter: string,
        storedDates: {
            id: string;
            display: string;
            term: moment.Moment | string;
        }[],
        isDefault: boolean
    ) => void;
};

const AdvancedFiltersContext = createContext<
    AdvancedFiltersContextType | undefined
>(undefined);

export const AdvancedFiltersProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [advancedFilters, setAdvancedFilters] = useState<
        Partial<AdvancedFiltersState>
    >({});
    const [totalAdvancedFilterCount, setTotalAdvancedFilterCount] = useState<
        Record<string, number>
    >({});
    const [sectionExpansionStatus, setSectionExpansionStatus] = useState<
        Record<string, boolean>
    >({});

    const updateFilters = (
        target: string,
        newFilters: DataGridAdvancedFilter | DataGridAdvancedFilter[] | null
    ) => {
        setAdvancedFilters(prevState => {
            const updatedFilters = { ...prevState };
            delete updatedFilters[target];

            if (newFilters) {
                return { ...updatedFilters, [target]: newFilters };
            }

            return updatedFilters;
        });
    };

    const handleDropdownSelectCheckboxChange = (
        values: DropDownSelect[],
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        const currentFilter = advancedFilters[target];
        const currentFiltersLength = Array.isArray(currentFilter)
            ? currentFilter.length
            : 0;

        if (values?.length === 0 || currentFiltersLength === values.length) {
            updateFilters(target, null);
            return;
        }

        const newFilters = values.map(item => ({
            form: { ...item },
            display: `${display} - ${item.label}`,
            term: item.value,
            ...(isDefault ? { isDefault: true } : {}),
        }));

        updateFilters(target, newFilters);
    };

    const handleFormSelectChangeMultiple = (
        values: FilterOption[],
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!values || values.length === 0) {
            updateFilters(target, null);
            return;
        }

        const newFilters = values.map(item => ({
            form: { ...item },
            display: `${display} - ${item.display}`,
            term: item.id,
            ...(isDefault ? { isDefault: true } : {}),
        }));

        updateFilters(target, newFilters);
    };

    const handleFormSelectChangeSingle = (
        value: FilterOption | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }

        const newFilter = {
            form: { ...value },
            display: `${display} - ${value.display}`,
            term: value.id,
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleCheckboxGroupChange = (
        values: string[],
        target: string,
        display?: string,
        options?: FilterOption[],
        isDefault?: boolean
    ) => {
        if (!values || values.length === 0) {
            updateFilters(target, null);
            return;
        }

        const newFilters = values.map(item => {
            const selectedFilter = options?.find(option => option.id === item);

            return {
                form: selectedFilter,
                display: `${display} - ${selectedFilter?.display}`,
                term: item,
                ...(isDefault ? { isDefault: true } : {}),
            };
        });

        updateFilters(target, newFilters);
    };

    const handleCheckboxChange = (
        value: boolean | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }

        const newFilter = {
            display: `${display}`,
            term: value,
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleDateChange = (
        date: moment.Moment | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!date) {
            updateFilters(target, null);
            return;
        }

        const formattedDate = isMoment(date) ? date.toISOString() : date;

        const newFilter = {
            display: `${display} - ${displayDate(formattedDate)}`,
            term: formattedDate,
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleGreaterLessThanChange = (
        value: string | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }

        const newFilter = {
            display: `${display} - ${value}`,
            term: value,
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleRadioButtonGroupChange = (
        value: string | number | boolean | null,
        target: string,
        display?: string,
        isDefault?: boolean,
        overrideDisplay?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }

        const displayValue =
            overrideDisplay && display ? display : `${display} - ${value}`;

        const newFilter = {
            display: displayValue,
            term: value,
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleZipCodeDistanceChange = (
        zip: string | null,
        distance: { id: number; display: string } | null,
        target: string,
        isDefault?: boolean
    ) => {
        if (!zip || !distance) {
            updateFilters(target, null);
            return;
        }

        const newFilter = {
            display: `${distance.display} from Zip - ${zip}`,
            term: zip,
            distance,
            ...(isDefault ? { isDefault: true } : {}),
        } as DataGridAdvancedFilter;

        updateFilters(target, newFilter);
    };

    const handleToggleButtonGroupChange = (
        value: { id: string; display: string; value?: boolean } | null,
        target: string,
        display?: string,
        isDefault?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }

        const newFilter = {
            display: `${display} - ${value.display}`,
            term: value.value ?? value.id,
            form: { ...value },
            ...(isDefault ? { isDefault: true } : {}),
        };

        updateFilters(target, newFilter);
    };

    const handleTextInputChange = (
        value: string | number | null,
        target: string,
        display?: string,
        isDefault?: boolean,
        overrideDisplay?: boolean
    ) => {
        if (!value) {
            updateFilters(target, null);
            return;
        }
        const displayValue =
            overrideDisplay && display ? display : `${display} - ${value}`;

        const newFilter = {
            display: displayValue,
            term: value,
            ...(isDefault ? { isDefault: true } : {}),
        };
        updateFilters(target, newFilter);
    };

    const handleCustomRangeDefaultChange = (
        value: string,
        target: string,
        display: string,
        dateKeys: string[],
        isDefault: boolean,
        isClear?: boolean
    ) => {
        const defaultFilter = {
            display: `${display} - ${value}`,
            term: value,
            ...(isDefault ? { isDefault: true } : {}),
        };

        setAdvancedFilters(prevState => {
            const updatedFilters = { ...prevState };
            dateKeys.forEach(key => delete updatedFilters[key]);
            if (!isClear) {
                return { ...updatedFilters, [target]: defaultFilter };
            }
            delete updatedFilters[target];
            return updatedFilters;
        });
    };

    const handleCustomRangeChange = (
        defaultFilter: string,
        storedDates: {
            id: string;
            display: string;
            term: moment.Moment | string;
        }[],
        isDefault: boolean
    ) => {
        const dateFilters: Record<string, { display: string; term: string }> =
            {};

        storedDates.forEach(storedDate => {
            const { id, display, term } = storedDate;
            const formattedDate = isMoment(term) ? term.toISOString() : term;

            dateFilters[id] = {
                display: `${display} - ${displayDate(formattedDate)}`,
                term: formattedDate,
                ...(isDefault ? { isDefault: true } : {}),
            };
        });

        setAdvancedFilters(prevState => {
            const updatedFilters = { ...prevState };
            delete updatedFilters[defaultFilter];

            return { ...updatedFilters, ...dateFilters };
        });
    };

    const contextValue = useMemo(
        () => ({
            advancedFilters,
            setAdvancedFilters,
            totalAdvancedFilterCount,
            setTotalAdvancedFilterCount,
            sectionExpansionStatus,
            setSectionExpansionStatus,
            handleDropdownSelectCheckboxChange,
            handleFormSelectChangeMultiple,
            handleFormSelectChangeSingle,
            handleCheckboxGroupChange,
            handleCheckboxChange,
            handleDateChange,
            handleGreaterLessThanChange,
            handleRadioButtonGroupChange,
            handleZipCodeDistanceChange,
            handleToggleButtonGroupChange,
            handleTextInputChange,
            handleCustomRangeDefaultChange,
            handleCustomRangeChange,
        }),
        [advancedFilters, sectionExpansionStatus, totalAdvancedFilterCount]
    );

    return (
        <AdvancedFiltersContext.Provider value={contextValue}>
            {children}
        </AdvancedFiltersContext.Provider>
    );
};

export const useAdvancedFilters = () => {
    const context = useContext(AdvancedFiltersContext);
    if (!context) {
        throw new Error(
            'useAdvancedFilters must be used within an AdvancedFiltersProvider'
        );
    }
    return context;
};
